import type { ReactNode } from "react";

export type AuditModule =
  | "dashboard"
  | "workpapers"
  | "analytics"
  | "collaboration"
  | "quality"
  | "methodology";
const modules: { key: AuditModule; label: string; icon: string }[] = [
  { key: "dashboard", label: "Dashboard", icon: "dashboard" },
  { key: "workpapers", label: "Engagements", icon: "folder" },
  { key: "analytics", label: "Data analytics", icon: "chart" },
  { key: "collaboration", label: "Collaborate", icon: "message" },
  { key: "quality", label: "Quality management", icon: "shield" },
  { key: "methodology", label: "Methodology", icon: "book" },
];
export function AuditIcon({ name }: { name: string }) {
  const paths: Record<string, ReactNode> = {
    dashboard: (
      <>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </>
    ),
    folder: <path d="M3 7V5a1 1 0 0 1 1-1h5l2 3h9a1 1 0 0 1 1 1v11H3V7Z" />,
    chart: (
      <>
        <path d="M4 3v17h17M8 15v-4m5 4V7m5 8V4" />
      </>
    ),
    message: <path d="M4 4h16v12H9l-5 4V4Zm4 4h8M8 12h5" />,
    shield: <path d="m12 3 8 3v6c0 5-8 9-8 9s-8-4-8-9V6l8-3Zm-4 9 3 3 5-6" />,
    book: <path d="M12 5v15M3 4h6l3 2 3-2h6v14h-6l-3 2-3-2H3V4Z" />,
  };
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name] ?? paths.folder}
    </svg>
  );
}
export function AuditPlatformShell({
  module,
  fileName,
  userName,
  onModule,
  onSuite,
  onLegacy,
  children,
}: {
  module: AuditModule;
  fileName?: string;
  userName: string;
  onModule: (module: AuditModule) => void;
  onSuite: () => void;
  onLegacy: () => void;
  children: ReactNode;
}) {
  const label = modules.find((item) => item.key === module)?.label;
  return (
    <div className="afi-root afb-root">
      <aside className="afb-sidebar">
        <button
          className="afb-brand"
          onClick={() => onModule("dashboard")}
          aria-label="AuditFlow dashboard"
        >
          <span className="afb-brand-mark">
            a<span>f</span>
          </span>
          <span>
            AuditFlow<small>BY LEDZERLY</small>
          </span>
        </button>
        <div className="afb-workspace-label">
          <span className="afb-live-dot" /> Audit workspace
        </div>
        <nav aria-label="AuditFlow modules">
          <span className="afb-nav-caption">WORKSPACE</span>
          {modules.map((item) => (
            <button
              key={item.key}
              aria-current={module === item.key ? "page" : undefined}
              className={module === item.key ? "active" : ""}
              onClick={() => onModule(item.key)}
            >
              <AuditIcon name={item.icon} />
              <span>{item.label}</span>
              {module === item.key && <span className="afb-nav-dot" />}
            </button>
          ))}
        </nav>
        <div className="afb-sidebar-bottom">
          <div className="afb-methodology-label">
            <AuditIcon name="shield" />
            <div>
              India methodology
              <small>AS · Ind AS · Standards on Auditing</small>
            </div>
          </div>
          <button onClick={onLegacy}>
            Other engagement types <span aria-hidden="true">↗</span>
          </button>
          <button onClick={onSuite}>← Ledzerly suite</button>
        </div>
      </aside>
      <div className="afb-body">
        <header className="afb-topbar">
          <div className="afb-breadcrumb">
            Workspace <span>/</span> <strong>{label}</strong>
            {fileName && (
              <>
                <span>/</span>
                <span className="afb-crumb-client">{fileName}</span>
              </>
            )}
          </div>
          <div className="afb-user">
            <span className="afb-avatar">
              {userName
                .trim()
                .split(/\s+/)
                .slice(0, 2)
                .map((s) => s[0])
                .join("")
                .toUpperCase()}
            </span>
            <span>
              {userName}
              <small>AuditFlow workspace</small>
            </span>
          </div>
        </header>
        <div className="afb-content">{children}</div>
      </div>
    </div>
  );
}
